import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ExperienceCard } from './ExperienceCard';
import type { Experience } from '@/lib/api';

interface ExperienceCarouselProps {
  experiences: Experience[];
  className?: string;
}

/**
 * ExperienceCarousel - Horizontal scrollable carousel for mobile
 * 
 * Accessibility features:
 * - Keyboard navigation (arrow keys)
 * - Screen reader announcements
 * - Focus management
 * - Reduced motion support
 * - Touch gesture support
 */
export function ExperienceCarousel({ experiences, className }: ExperienceCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isReducedMotion, setIsReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Update scroll state
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

    // Calculate current index for screen reader announcement
    const cardWidth = container.firstElementChild?.clientWidth || 300;
    const newIndex = Math.round(scrollLeft / cardWidth);
    setCurrentIndex(Math.min(newIndex, experiences.length - 1));
  }, [experiences.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = container.firstElementChild?.clientWidth || 300;
    const gap = 16; // gap-4 = 1rem = 16px
    const scrollAmount = cardWidth + gap;

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: isReducedMotion ? 'auto' : 'smooth',
    });
  }, [isReducedMotion]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scroll('left');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scroll('right');
    }
  }, [scroll]);

  if (experiences.length === 0) {
    return null;
  }

  return (
    <div 
      className={cn('relative', className)}
      role="region"
      aria-label="Experience carousel"
      aria-roledescription="carousel"
    >
      {/* Screen reader live region for current slide */}
      <div 
        className="sr-only" 
        aria-live="polite" 
        aria-atomic="true"
      >
        Showing experience {currentIndex + 1} of {experiences.length}
      </div>

      {/* Navigation Buttons */}
      <button
        type="button"
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        className={cn(
          'absolute left-0 top-1/2 z-10 -translate-y-1/2 -translate-x-2',
          'flex h-10 w-10 items-center justify-center rounded-full',
          'bg-background/95 backdrop-blur border shadow-lg',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          canScrollLeft 
            ? 'opacity-100 hover:bg-muted hover:scale-105' 
            : 'opacity-0 pointer-events-none'
        )}
        aria-label="Previous experience"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        className={cn(
          'absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-2',
          'flex h-10 w-10 items-center justify-center rounded-full',
          'bg-background/95 backdrop-blur border shadow-lg',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          canScrollRight 
            ? 'opacity-100 hover:bg-muted hover:scale-105' 
            : 'opacity-0 pointer-events-none'
        )}
        aria-label="Next experience"
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>

      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="group"
        aria-label="Experience cards"
        className={cn(
          'flex gap-4 overflow-x-auto px-1 py-2',
          'scrollbar-hide snap-x snap-mandatory',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl',
          // Hide scrollbar across browsers
          '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
        )}
        style={{ scrollPaddingLeft: '4px', scrollPaddingRight: '4px' }}
      >
        {experiences.map((experience, index) => (
          <div
            key={experience.id}
            className="flex-shrink-0 w-[280px] sm:w-[320px] snap-start"
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${experiences.length}`}
          >
            <ExperienceCard experience={experience} layout="vertical" />
          </div>
        ))}
      </div>

      {/* Pagination Dots */}
      <div 
        className="flex justify-center gap-1.5 mt-4"
        role="tablist"
        aria-label="Carousel pagination"
      >
        {experiences.map((_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={index === currentIndex}
            aria-label={`Go to experience ${index + 1}`}
            onClick={() => {
              const container = scrollContainerRef.current;
              if (!container) return;
              const cardWidth = container.firstElementChild?.clientWidth || 300;
              const gap = 16;
              container.scrollTo({
                left: index * (cardWidth + gap),
                behavior: isReducedMotion ? 'auto' : 'smooth',
              });
            }}
            className={cn(
              'h-2 rounded-full transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              index === currentIndex
                ? 'w-6 bg-primary'
                : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
            )}
          />
        ))}
      </div>
    </div>
  );
}
